import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckIcon,
  GlobeIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  LayoutTemplate,
  Move,
  FileText,
  Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useBrandSettings, useBrandFonts } from "../../hooks/useBrandSettings";
import { useServiceMenu } from "../../hooks/useServiceMenu";
import { useStaticPages } from "../../hooks/useStaticPages";
import type { BrandSettings } from "../../types";

import { CompanyStep } from "./CompanyStep";
import { BrandColors } from "./BrandColors";
import { CoverLetterEditor } from "./CoverLetterEditor";
import { StaticPageEditor } from "./StaticPageEditor";
import { ServiceMenuBuilder } from "./ServiceMenuBuilder";
import { SetupPreview } from "./SetupPreview";

type SetupStep =
  | "company"
  | "branding"
  | "cover-letter"
  | "static-pages"
  | "service-menu"
  | "preview";

type BuiltInFontPreference = "inter" | "roboto" | "lato" | "open-sans";
type TypographySelection = {
  source?: "built_in" | "uploaded";
  builtInFont?: BuiltInFontPreference | null;
  uploadedFontId?: string | null;
  uploadedFontName?: string | null;
};

const STEPS: { id: SetupStep; label: string; description: string }[] = [
  { id: "company", label: "Company", description: "Name, contact info, address" },
  { id: "branding", label: "Branding", description: "Logo, colors, fonts, assets" },
  { id: "cover-letter", label: "Cover letter", description: "Voice and intro copy" },
  { id: "static-pages", label: "Pages", description: "Reusable proposal pages" },
  { id: "service-menu", label: "Services", description: "Default service catalog" },
  { id: "preview", label: "Preview", description: "Review, builder choice, publish" },
];

function hasCompanyStepData(brand: BrandSettings | null | undefined) {
  if (!brand) return false;

  return Boolean(
    brand.company_name ||
      brand.phone ||
      brand.email ||
      brand.website ||
      brand.address_line1 ||
      brand.city ||
      brand.state ||
      brand.zip
  );
}

function hasBrandingStepData(brand: BrandSettings | null | undefined) {
  if (!brand) return false;

  return Boolean(
    brand.logo_url ||
      brand.default_logo_asset_id ||
      brand.primary_color ||
      brand.accent_color ||
      brand.background_color ||
      brand.text_color ||
      brand.font_preference ||
      brand.default_font_id
  );
}

function hasCoverLetterStepData(brand: BrandSettings | null | undefined) {
  if (!brand) return false;

  return Boolean(
    brand.cover_letter_html || brand.signoff || brand.suggested_tagline
  );
}

function deriveCompletedSteps(brand: BrandSettings | null | undefined): Set<SetupStep> {
  const completed = new Set<SetupStep>();

  if (hasCompanyStepData(brand)) completed.add("company");
  if (hasBrandingStepData(brand)) completed.add("branding");
  if (hasCoverLetterStepData(brand)) completed.add("cover-letter");
  if (brand?.setup_complete) completed.add("preview");

  return completed;
}

function getFirstIncompleteStep(completed: Set<SetupStep>): SetupStep {
  return STEPS.find((step) => !completed.has(step.id))?.id ?? "preview";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function coerceBuiltInFont(value: unknown): BuiltInFontPreference | null {
  return value === "inter" ||
    value === "roboto" ||
    value === "lato" ||
    value === "open-sans"
    ? value
    : null;
}

function builtInFontFamily(font: BuiltInFontPreference) {
  switch (font) {
    case "roboto":
      return "Roboto, Inter, system-ui, sans-serif";
    case "lato":
      return "Lato, Inter, system-ui, sans-serif";
    case "open-sans":
      return "\"Open Sans\", Inter, system-ui, sans-serif";
    case "inter":
    default:
      return "Inter, system-ui, sans-serif";
  }
}

function resolveTypographySelections(
  brand: BrandSettings | null | undefined
): {
  heading: TypographySelection;
  body: TypographySelection;
} {
  const fallback = coerceBuiltInFont(brand?.font_preference) ?? "inter";
  const typography = isRecord(brand?.preview_preferences?.typography)
    ? (brand?.preview_preferences?.typography as Record<string, unknown>)
    : null;

  const fallbackSelection: TypographySelection = {
    source: "built_in",
    builtInFont: fallback,
  };

  const heading = isRecord(typography?.heading)
    ? (typography?.heading as TypographySelection)
    : fallbackSelection;

  const body = isRecord(typography?.body)
    ? (typography?.body as TypographySelection)
    : fallbackSelection;

  return { heading, body };
}

function resolveFontFamily(
  selection: TypographySelection,
  uploadedFonts: Array<{ id: string; family_name: string | null; public_url: string | null }>,
  fallback: BuiltInFontPreference
) {
  if (selection.source === "uploaded" && selection.uploadedFontId) {
    const match = uploadedFonts.find((font) => font.id === selection.uploadedFontId);
    if (match?.family_name) {
      return `"${match.family_name}", Inter, system-ui, sans-serif`;
    }
  }

  return builtInFontFamily(selection.builtInFont ?? fallback);
}

function PreviewShell({ brand }: { brand: BrandSettings | null | undefined }) {
  const { data: brandFonts = [] } = useBrandFonts();

  const companyName = brand?.company_name?.trim() || "Your Company Name";
  const tagline =
    brand?.tagline?.trim() || "Your proposal preview updates as you build.";
  const primaryColor = brand?.primary_color || "#111827";
  const accentColor = brand?.accent_color || "#FF6B00";
  const backgroundColor = brand?.background_color || "#FFFFFF";
  const textColor = brand?.text_color || "#111827";
  const mutedColor = brand?.muted_color || "#6B7280";
  const footerText =
    brand?.footer_text?.trim() || "Prepared professionally by your team.";

  const fallbackFont = coerceBuiltInFont(brand?.font_preference) ?? "inter";
  const typography = resolveTypographySelections(brand);

  const headingFontFamily = resolveFontFamily(
    typography.heading,
    brandFonts,
    fallbackFont
  );
  const bodyFontFamily = resolveFontFamily(
    typography.body,
    brandFonts,
    fallbackFont
  );

  const companyAddress = [
    brand?.address_line1,
    brand?.address_line2,
    [brand?.city, brand?.state].filter(Boolean).join(", "),
    brand?.zip,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="sticky top-0 h-screen overflow-y-auto border-l border-white/10 bg-[#0b0b0b] px-6 py-8">
      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-[0.24em] text-white/35">
          Live proposal preview
        </p>
        <h3 className="mt-2 text-sm font-semibold text-white">Brand setup preview</h3>
        <p className="mt-1 text-xs leading-5 text-white/45">
          This is how your brand will appear in standard proposals. The canvas builder
          gives you full layout control on top of this same foundation.
        </p>
      </div>

      <div className="rounded-[28px] border border-white/10 bg-[#111111] p-3 shadow-2xl">
        <div className="mx-auto w-full max-w-[760px] overflow-hidden rounded-[22px] bg-white shadow-2xl">
          <div
            className="min-h-[980px]"
            style={{
              background: backgroundColor,
              color: textColor,
              fontFamily: bodyFontFamily,
            }}
          >
            <div
              className="px-10 pb-10 pt-12"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                color: "#ffffff",
              }}
            >
              <div className="mb-16 flex items-start justify-between gap-6">
                <div className="min-w-0">
                  {brand?.logo_url ? (
                    <img
                      src={brand.logo_url}
                      alt={companyName}
                      className="mb-4 h-14 w-auto max-w-[160px] rounded-lg object-contain"
                    />
                  ) : null}

                  <h1
                    className="max-w-[420px] text-4xl font-bold leading-tight"
                    style={{ fontFamily: headingFontFamily }}
                  >
                    {companyName}
                  </h1>

                  <p
                    className="mt-3 max-w-[420px] text-sm leading-6 text-white/80"
                    style={{ fontFamily: bodyFontFamily }}
                  >
                    {tagline}
                  </p>
                </div>

              </div>

              <div
                className="grid gap-3 text-sm text-white/80"
                style={{ fontFamily: bodyFontFamily }}
              >
                {brand?.show_company_phone && brand?.phone ? (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4" />
                    <span>{brand.phone}</span>
                  </div>
                ) : null}

                {brand?.show_company_email && brand?.email ? (
                  <div className="flex items-center gap-2">
                    <MailIcon className="h-4 w-4" />
                    <span>{brand.email}</span>
                  </div>
                ) : null}

                {brand?.show_company_website && brand?.website ? (
                  <div className="flex items-center gap-2">
                    <GlobeIcon className="h-4 w-4" />
                    <span>{brand.website}</span>
                  </div>
                ) : null}

                {brand?.show_company_address && companyAddress ? (
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="mt-0.5 h-4 w-4" />
                    <span>{companyAddress}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="px-10 py-10">
              <div className="grid gap-8">
                <section>
                  <p
                    className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                    style={{ color: accentColor, fontFamily: headingFontFamily }}
                  >
                    Cover letter preview
                  </p>
                  <h2
                    className="mt-2 text-2xl font-semibold"
                    style={{ fontFamily: headingFontFamily }}
                  >
                    Introduction
                  </h2>
                  <div
                    className="mt-4 space-y-4 text-sm leading-7"
                    style={{ color: textColor, fontFamily: bodyFontFamily }}
                  >
                    {brand?.cover_letter_html ? (
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: brand.cover_letter_html }}
                      />
                    ) : (
                      <>
                        <p>
                          Thank you for the opportunity to provide this proposal. As you
                          complete your brand setup, this area will begin to reflect your
                          company voice, colors, logos, pages, and reusable proposal structure.
                        </p>
                        <p style={{ color: mutedColor }}>
                          Add your cover letter, pages, branding assets, fonts, and service
                          catalog to shape how every future template and proposal will look.
                        </p>
                      </>
                    )}
                  </div>
                </section>

                <section className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-black/10 p-5">
                    <p
                      className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                      style={{ color: accentColor, fontFamily: headingFontFamily }}
                    >
                      Brand colors
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                      <div
                        className="h-10 w-10 rounded-full border border-black/10"
                        style={{ backgroundColor: primaryColor }}
                      />
                      <div
                        className="h-10 w-10 rounded-full border border-black/10"
                        style={{ backgroundColor: accentColor }}
                      />
                      <div
                        className="h-10 w-10 rounded-full border border-black/10"
                        style={{ backgroundColor: backgroundColor }}
                      />
                    </div>
                    <p
                      className="mt-4 text-xs leading-6"
                      style={{ color: mutedColor, fontFamily: bodyFontFamily }}
                    >
                      Your selected palette will flow into covers, headings, accents, and
                      future builder layouts.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-black/10 p-5">
                    <p
                      className="text-[11px] font-semibold uppercase tracking-[0.18em]"
                      style={{ color: accentColor, fontFamily: headingFontFamily }}
                    >
                      Proposal structure
                    </p>
                    <ul
                      className="mt-4 space-y-2 text-sm"
                      style={{ color: textColor, fontFamily: bodyFontFamily }}
                    >
                      <li>• Branded cover page</li>
                      <li>• Cover letter</li>
                      <li>• Services and pricing</li>
                      <li>• Reusable or fixed pages</li>
                    </ul>
                    <p
                      className="mt-4 text-xs leading-6"
                      style={{ color: mutedColor, fontFamily: bodyFontFamily }}
                    >
                      Templates and future drag-and-drop layouts will build on this same
                      setup foundation.
                    </p>
                  </div>
                </section>
              </div>
            </div>

            <div
              className="border-t px-10 py-5 text-xs"
              style={{
                borderColor: `${mutedColor}33`,
                color: mutedColor,
                fontFamily: bodyFontFamily,
              }}
            >
              {footerText}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SetupWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: brand, updateBrandSettings, isUpdating } = useBrandSettings();
  const { data: services = [] } = useServiceMenu(false);
  const { data: pages = [] } = useStaticPages();

  const [currentStep, setCurrentStep] = useState<SetupStep>("company");
  const [completedSteps, setCompletedSteps] = useState<Set<SetupStep>>(new Set());

  useEffect(() => {
    const derivedCompleted = deriveCompletedSteps(brand);
    setCompletedSteps(derivedCompleted);

    if (!brand) return;

    setCurrentStep(
      brand.setup_complete ? "preview" : getFirstIncompleteStep(derivedCompleted)
    );
  }, [brand]);

  // Mark pages/services steps complete when data exists (persists across reloads)
  useEffect(() => {
    if (services.length > 0) {
      setCompletedSteps((prev) => {
        if (prev.has("service-menu")) return prev;
        const next = new Set(prev);
        next.add("service-menu");
        return next;
      });
    }
  }, [services.length]);

  useEffect(() => {
    const activePages = pages.filter((p) => p.active);
    if (activePages.length > 0) {
      setCompletedSteps((prev) => {
        if (prev.has("static-pages")) return prev;
        const next = new Set(prev);
        next.add("static-pages");
        return next;
      });
    }
  }, [pages]);

  const currentIndex = useMemo(() => {
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    return idx >= 0 ? idx : 0;
  }, [currentStep]);

  const progressCount = useMemo(() => {
    const baseCount = completedSteps.size;
    return brand?.setup_complete ? STEPS.length : baseCount;
  }, [brand?.setup_complete, completedSteps]);

  const goToStep = useCallback((step: SetupStep) => {
    setCurrentStep(step);
    window.scrollTo(0, 0);
  }, []);

  const completeStep = useCallback((step: SetupStep) => {
    const nextIndex = STEPS.findIndex((s) => s.id === step) + 1;

    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.add(step);
      return next;
    });

    if (nextIndex < STEPS.length) {
      setTimeout(() => {
        setCurrentStep(STEPS[nextIndex].id);
        window.scrollTo(0, 0);
      }, 150);
    }
  }, []);

  const finishSetup = useCallback(async () => {
    await updateBrandSettings({
      setup_complete: true,
    });

    await qc.invalidateQueries({
      queryKey: ["brand-settings"],
    });

    navigate({
      to: "/proposal-builder",
      replace: true,
    });
  }, [navigate, qc, updateBrandSettings]);

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="grid min-h-screen grid-cols-1 xl:grid-cols-[256px_minmax(0,1fr)_460px]">
        <aside className="flex flex-col border-b border-white/10 px-6 py-10 xl:border-b-0 xl:border-r">
          <div className="mb-10">
            <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-white/40">
              SizilPro
            </p>
            <h1 className="text-lg font-semibold leading-tight">Proposal Setup</h1>
            <p className="mt-1 text-xs text-white/50">
              Complete once, use across templates and proposals.
            </p>
          </div>

          <nav className="flex flex-col gap-1">
            {STEPS.map((step, idx) => {
              const isCompleted =
                completedSteps.has(step.id) ||
                (brand?.setup_complete && step.id === "preview");
              const isCurrent = step.id === currentStep;
              const isAccessible = idx <= currentIndex || isCompleted;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => isAccessible && goToStep(step.id)}
                  disabled={!isAccessible}
                  className={cn(
                    "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all",
                    isCurrent && "bg-white/10 text-white",
                    !isCurrent && isAccessible && "text-white/50 hover:bg-white/5 hover:text-white",
                    !isAccessible && "cursor-not-allowed text-white/20"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold transition-all",
                      isCompleted && "border-emerald-500 bg-emerald-500 text-white",
                      isCurrent && !isCompleted && "border-white bg-white text-black",
                      !isCurrent && !isCompleted && "border-white/20 text-white/30"
                    )}
                  >
                    {isCompleted ? <CheckIcon className="h-3 w-3" /> : idx + 1}
                  </span>

                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium leading-tight">
                      {step.label}
                    </span>
                    <span className="truncate text-[11px] leading-tight text-white/35">
                      {step.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">
              What setup controls
            </p>

            <div className="mt-3 space-y-2 text-sm text-white/75">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-white/35" />
                <span>Brand identity and colors</span>
              </div>
              <div className="flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4 text-white/35" />
                <span>Template foundation</span>
              </div>
              <div className="flex items-center gap-2">
                <Move className="h-4 w-4 text-white/35" />
                <span>Future builder direction</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-white/35" />
                <span>Proposal preview and PDF styling</span>
              </div>
            </div>
          </div>

          <div className="mt-auto pt-6">
            <div className="mb-2 flex justify-between text-[11px] text-white/30">
              <span>Progress</span>
              <span>
                {progressCount}/{STEPS.length}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${(progressCount / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        </aside>

        <main className="min-w-0 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-6 py-10 md:px-8">
            <div className="mb-8">
              <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-white/30">
                Step {currentIndex + 1} of {STEPS.length}
              </p>
              <h2 className="text-2xl font-bold">{STEPS[currentIndex].label}</h2>
              <p className="mt-1 text-sm text-white/50">
                {STEPS[currentIndex].description}
              </p>
            </div>

            <div key={currentStep}>
              {currentStep === "company" && (
                <CompanyStep onComplete={() => completeStep("company")} />
              )}

              {currentStep === "branding" && (
                <BrandColors onComplete={() => completeStep("branding")} />
              )}

              {currentStep === "cover-letter" && (
                <CoverLetterEditor onComplete={() => completeStep("cover-letter")} />
              )}

              {currentStep === "static-pages" && (
                <StaticPageEditor onComplete={() => completeStep("static-pages")} />
              )}

              {currentStep === "service-menu" && (
                <ServiceMenuBuilder onComplete={() => completeStep("service-menu")} />
              )}

              {currentStep === "preview" && (
                <SetupPreview
                  onComplete={() => {
                    void finishSetup();
                  }}
                  isCompleting={isUpdating}
                />
              )}
            </div>
          </div>
        </main>

        <div className="hidden xl:block">
          <PreviewShell brand={brand} />
        </div>
      </div>
    </div>
  );
}